import React from "react";
import styled, { css } from "styled-components";

const CARD_FLIP_TIME = 128;

const Flipper = styled.div`
  position: relative;
  transform-style: preserve-3d;
  width: 100%;
  height: 100%;
  transform: rotateX(${(props) => props.$deg}deg);
  will-change: transform;

  ${(props) =>
    props.$flipping &&
    css`
      transition: transform ${CARD_FLIP_TIME}ms linear;
    `}
`;

const CardInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: ${(props) => props.$color || "#ffeb3b"};
  font-family: Arial, "sans-serif";
  font-size: clamp(16px, 2.4vw, 38px);
  backface-visibility: hidden;

  /* Mobile: reduzir fonte para caber na tela */
  @media (max-width: 640px) {
    font-size: 22px;
  }

  ${(props) =>
    props.$facing === "back" &&
    css`
      transform: rotateX(180deg);
    `}

  ${(props) =>
    props.$facing === "front" &&
    css`
      z-index: 2;
      transform: rotateX(0deg);
    `}
`;

const CardContainer = styled.div`
  position: relative;
  width: clamp(22px, 3.2vw, 35px);
  height: clamp(28px, 4vw, 45px);
  border: 4px solid #363636;
  background-color: #252525;
  box-shadow: rgba(0, 0, 0, 1) 3px 3px 10px inset;
  perspective: 1000px;

  /* Mobile: reduzir tamanho das cartas */
  @media (max-width: 640px) {
    border-width: 3px;
  }

  &:not(:last-child) {
    border-right: 0;
  }

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    width: 100%;
    height: 2px;
    background-color: #050505;
  }
`;

const Flight = styled.div`
  display: flex;

  /* Mobile: permitir quebra e uso de toda largura */
  @media (max-width: 640px) {
    flex-wrap: wrap;
    width: 100%;
  }
`;

const Board = styled.div`
  display: flex;
  margin-bottom: 4px;

  /* Mobile: empilhar colunas e adicionar espaçamento */
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }
`;

const LineContainer = styled.div`
  display: flex;
  flex-direction: column;

  /* Mobile: ocupar largura total e reduzir padding do header interno */
  @media (max-width: 640px) {
    width: 100%;
  }
`;

const Header = styled.div`
  width: 100%;
  padding: 10px;
  text-align: center;
  font-family: Arial;
  font-size: clamp(14px, 2.2vw, 26px);
  color: white;

  /* Mobile: reduzir fonte e padding do título */
  @media (max-width: 640px) {
    padding: 6px;
  }
`;

const Cards = styled.div`
  display: flex;

  /* Mobile: permitir quebra e centralizar */
  @media (max-width: 640px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

class CardChar {
  static MODES = {
    LETTERS: 0, // A-Z e espaço
    NUMBERS: 1, // 0-9 e espaço
    NUMBERS_WITH_DECIMAL: 2, // 0-9, ., , e espaço
  };

  constructor(char = "", mode = CardChar.MODES.LETTERS) {
    this.mode = mode; // Agora usando propriedade regular
    this._current = this.codeFromChar(char.toString().toUpperCase());
  }

  get mode() {
    return this._mode;
  }

  set mode(value) {
    this._mode = value;
    this._chars = this.getCharsForMode(value);
  }

  getCharsForMode(mode) {
    switch (mode) {
      case CardChar.MODES.NUMBERS:
        return [" ", ..."0123456789."];
      case CardChar.MODES.NUMBERS_WITH_DECIMAL:
        return [" ", ..."0123456789."];
      case CardChar.MODES.LETTERS:
      default:
        return [" ", ..."1ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
    }
  }

  get CHARS() {
    return this._chars || this.getCharsForMode(this._mode);
  }

  get current() {
    return this.CHARS[this._current] || " ";
  }

  set current(char) {
    const index = this.CHARS.indexOf(char.toString().toUpperCase());
    if (index !== -1) this._current = index;
  }

  equals(char) {
    return this.current === char.toString().toUpperCase();
  }

  codeFromChar = (char) =>
    Math.max(0, this.CHARS.indexOf(char.toString().toUpperCase()));

  next() {
    this._current = (this._current + 1) % this.CHARS.length;
  }

  nextChar() {
    return this.CHARS[(this._current + 1) % this.CHARS.length];
  }
}

class Card extends React.PureComponent {
  constructor(p) {
    super(p);

    // Força o modo baseado no tipo de campo
    const forceMode = p.isNumeric
      ? p.isDecimal
        ? CardChar.MODES.NUMBERS_WITH_DECIMAL
        : CardChar.MODES.NUMBERS
      : CardChar.MODES.LETTERS;

    const charA = new CardChar(p.char, forceMode);
    const charB = new CardChar(charA.nextChar(), forceMode);

    this.state = {
      prevPageKey: p.pageKey,
      charA,
      charB,
      flipping: false,
      deg: 0,
      atChar: true,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const flipped = Math.abs(this.state.deg) % 360 > 0;

    if (this.props.pageKey !== this.state.prevPageKey) {
      this.setState({
        prevPageKey: this.props.pageKey,
        flipping: true,
        deg: this.state.deg + 180,
      });

      setTimeout(() => {
        this.setState({ flipping: false });
      }, CARD_FLIP_TIME);

      return;
    }

    if (prevState.flipping && !this.state.flipping) {
      this.setState((state) => {
        const atChar = flipped
          ? state.charB.equals(this.props.char)
          : state.charA.equals(this.props.char);

        if (atChar) {
          const forceMode = this.props.isNumeric
            ? this.props.isDecimal
              ? CardChar.MODES.NUMBERS_WITH_DECIMAL
              : CardChar.MODES.NUMBERS
            : CardChar.MODES.LETTERS;

          const charA = new CardChar(this.props.char, forceMode);
          const charB = new CardChar(charA.nextChar(), forceMode);

          return { atChar, charA, charB, deg: 0 };
        }

        return {
          atChar: false,
          charA: flipped
            ? new CardChar(state.charB.nextChar(), state.charB.mode)
            : state.charA,
          charB: flipped
            ? state.charB
            : new CardChar(state.charA.nextChar(), state.charA.mode),
        };
      });
    } else if (
      ((!flipped && !this.state.charA.equals(this.props.char)) ||
        (flipped && !this.state.charB.equals(this.props.char))) &&
      !this.state.flipping
    ) {
      this.setState((state) => ({
        atChar: false,
        flipping: true,
        deg: state.deg + 180,
      }));
    } else if (!prevState.flipping && this.state.flipping) {
      setTimeout(() => {
        this.setState({ flipping: false });
      }, CARD_FLIP_TIME);
    }
  }

  render() {
    const { charA, charB, atChar, deg } = this.state;
    const { color } = this.props;

    return (
      <CardContainer>
        <Flipper $flipping={!atChar} $deg={deg}>
          <CardInner $facing="front" $color={color}>
            {charA.current}
          </CardInner>
          <CardInner $facing="back" $color={color}>
            {charB.current}
          </CardInner>
        </Flipper>
      </CardContainer>
    );
  }
}

class FlightLine extends React.PureComponent {
  getLine = () => {
    const { text = "", length } = this.props;
    const letters = text.toString().split("");

    return [
      ...letters,
      ...Array.from({ length: Math.max(0, length - letters.length) }),
    ];
  };

  render() {
    const {
      title,
      withTitle,
      lineColor,
      pageKey,
      isNumeric,
      isDecimal,
      ...props
    } = this.props;

    const mode = isDecimal
      ? CardChar.MODES.NUMBERS_WITH_DECIMAL
      : isNumeric
      ? CardChar.MODES.NUMBERS
      : CardChar.MODES.LETTERS;

    return (
      <Flight {...props}>
        <LineContainer>
          {withTitle && (
            <Header style={{ padding: "0px", paddingBottom: "10px" }}>
              {title}
            </Header>
          )}
          <Cards>
            {this.getLine().map((char, i) => (
              <Card
                key={`${title}-${i}-${pageKey}`}
                char={char || " "}
                color={lineColor}
                pageKey={pageKey}
                mode={mode} // Passando o modo corretamente
                isNumeric={isNumeric || isDecimal} // Para referência
              />
            ))}
          </Cards>
        </LineContainer>
      </Flight>
    );
  }
}

export default class FlipBoard extends React.Component {
  static defaultProps = {
    itemsPerPage: 16,
    autoFlipTime: 20000, // 20 segundos
    totalPages: 10,
    emptyItem: {
      emissora: "",
      destino: "",
      peso_total: "",
      frete_total: "",
    },
  };

  state = {
    items: Array.from({ length: this.props.itemsPerPage }, () => ({
      ...this.props.emptyItem,
    })),
    currentPage: 1,
    totalPages: 11, // Você pode ajustar ou obter da API
    isLoading: false,
  };

  componentDidMount() {
    this.loadData(this.state.currentPage);

    if (this.props.autoFlip) {
      this.setupAutoFlip();
    }
  }

  componentWillUnmount() {
    if (this.autoFlipTimer) {
      clearInterval(this.autoFlipTimer);
    }
  }

  setupAutoFlip = () => {
    if (this.autoFlipTimer) {
      clearInterval(this.autoFlipTimer);
    }

    this.autoFlipTimer = setInterval(() => {
      this.handlePageChange(
        ((this.state.currentPage + 1) % this.state.totalPages) === 0 ? 1 : (this.state.currentPage + 1) % this.state.totalPages
      );
    }, this.props.autoFlipTime);
  };

  loadData = async (page) => {
    this.setState({ isLoading: true });

    try {
      const data = await this.props.fetchData(page);
      this.setState({
        items:
          data.length > 0 ? data : Array.from({ length: this.props.itemsPerPage }, () => ({
                ...this.props.emptyItem,
              })),
        isLoading: false,
      });
    } catch (error) {
      console.error("Error loading data:", error);
      this.setState({ isLoading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, () => {
      this.loadData(page);
    });

    // Reinicia o auto-flip timer quando muda página manualmente
    if (this.props.autoFlip) {
      this.setupAutoFlip();
    }
  };

  render() {
    const { items, currentPage, isLoading } = this.state;
    const { columns, formatters, showPagination } = this.props;

    return (
      <div className="flex flex-col p-2 sm:p-4">
        <div className="flex flex-row flex-wrap gap-2">
          {items.map((item, i) => (
            <Board key={`item-${currentPage}-${i}`}>
              {columns.map((column, colIndex) => (
                <FlightLine
                  key={`col-${colIndex}-${i}`}
                  text={
                    column.formatter
                      ? column.formatter(item[column.key], formatters)
                      : item[column.key] || ""
                  }
                  title={column.title}
                  length={column.length}
                  withTitle={i === 0}
                  lineColor={i % 2 === 0 ? "#42a5f5" : "#ffffff"}
                  style={{ marginRight: "10px" }}
                  pageKey={currentPage}
                  isNumeric={column.isDecimal || column.isNumeric}
                  isDecimal={column.isDecimal}
                />
              ))}
            </Board>
          ))}
        </div>

        {showPagination && (
          <div className="flex justify-center mt-2 sm:mt-4">
            {Array.from({ length: this.state.totalPages }).map((_, i) => (
              <button
                key={`page-${i}`}
                onClick={() => this.handlePageChange(i)}
                disabled={isLoading}
                className={`mx-0.5 sm:mx-1 px-2 sm:px-3 py-1 rounded ${
                  currentPage === i ? "bg-blue-500 text-white" : "bg-gray-200"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
}
